import React, { useState } from 'react';
import { discography } from '../data/mock';
import { Play, ExternalLink, Calendar, X } from 'lucide-react';

const DiscographyMasonry = () => {
  const [selectedTrack, setSelectedTrack] = useState(null);

  const openPlayer = (track) => {
    setSelectedTrack(track);
  };

  const closePlayer = () => {
    setSelectedTrack(null);
  };

  const getEmbedUrl = (url) => {
    // Handle YouTube URLs
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      let videoId = '';
      if (url.includes('youtu.be/')) {
        videoId = url.split('youtu.be/')[1].split('?')[0];
      } else if (url.includes('watch?v=')) {
        videoId = url.split('v=')[1].split('&')[0];
      }
      return `https://www.youtube.com/embed/${videoId}`;
    }
    
    // Handle Spotify URLs
    if (url.includes('track/')) {
      const trackId = url.split('track/')[1].split('?')[0];
      return `https://open.spotify.com/embed/track/${trackId}`;
    } else if (url.includes('album/')) {
      const albumId = url.split('album/')[1].split('?')[0];
      return `https://open.spotify.com/embed/album/${albumId}`;
    }
    
    return url;
  };

  return (
    <section id="discography" className="py-16 sm:py-20 lg:py-24 bg-neutral-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 sm:mb-16">
          <h2 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold uppercase text-white mb-3 sm:mb-4 tracking-wide">
            Discographie
          </h2>
          <p className="text-gray-400 text-base sm:text-lg max-w-2xl mx-auto px-4">
            Découvrez mon parcours musical à travers mes sorties et collaborations
          </p>
        </div>

        {/* Masonry Grid - Taille uniforme */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {discography.sort((a, b) => b.year - a.year).map((release) => {
            return (
              <div
                key={release.id}
                className="group relative bg-neutral-800/50 backdrop-blur-sm border border-neutral-700 rounded-2xl overflow-hidden hover:border-rose-500/50 transition-all duration-300 hover:transform hover:-translate-y-1"
              >
                {/* Cover Image */}
                <div className="relative overflow-hidden h-80">
                  <img
                    src={release.cover}
                    alt={release.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent"></div>

                  {/* Play Button */}
                  <button
                    onClick={() => openPlayer(release)}
                    className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-16 h-16 bg-gradient-to-r from-rose-800 to-violet-800 rounded-full flex items-center justify-center hover:scale-110 transition-transform duration-300 shadow-lg hover:shadow-rose-500/50"
                  >
                    <Play size={28} className="text-white ml-1" />
                  </button>

                  {/* Type Badge */}
                  <div className="absolute top-4 left-4 px-3 py-1 bg-rose-800/80 backdrop-blur-sm border border-rose-500/30 text-white rounded-full text-xs uppercase tracking-wider font-semibold">
                    {release.type}
                  </div>

                  {/* Year */}
                  <div className="absolute top-4 right-4 flex items-center gap-1 text-white/80 text-sm">
                    <Calendar size={14} />
                    <span>{release.year}</span>
                  </div>
                </div>

                {/* Info */}
                <div className="p-6">
                  <h3 className="text-2xl font-bold text-white mb-2 group-hover:text-rose-400 transition-colors">
                    {release.title}
                  </h3>
                  <p 
                    className="text-gray-400 mb-4" 
                    dangerouslySetInnerHTML={{ __html: release.description.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }}
                  />
                  
                  <a
                    href={release.streamUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-rose-800 to-violet-800 text-white rounded-full font-medium uppercase text-sm tracking-wider hover:scale-105 transition-transform duration-300 shadow-lg hover:shadow-rose-500/50"
                  >
                    <Play size={18} />
                    Écouter
                  </a>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Player Modal */}
      {selectedTrack && (
        <div
          className="fixed inset-0 bg-black/95 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={closePlayer}
        >
          <button
            onClick={closePlayer}
            className="absolute top-8 right-8 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors duration-300"
          >
            <X size={24} className="text-white" />
          </button>
          <div className="max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            <div className="bg-neutral-900 rounded-xl overflow-hidden p-6">
              <div className="flex items-start gap-4 mb-4">
                <img 
                  src={selectedTrack.cover} 
                  alt={selectedTrack.title}
                  className="w-24 h-24 rounded-lg object-cover"
                />
                <div className="flex-1">
                  <h3 className="text-white text-2xl font-bold mb-2">{selectedTrack.title}</h3>
                  <p className="text-gray-400 text-sm">{selectedTrack.type} • {selectedTrack.year}</p>
                </div>
              </div>
              <iframe
                src={getEmbedUrl(selectedTrack.youtubeUrl || selectedTrack.streamUrl)}
                width="100%"
                height="352"
                frameBorder="0"
                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                loading="lazy"
                className="rounded-lg"
                title={selectedTrack.title}
              ></iframe>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default DiscographyMasonry;
